import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api/v1';
const EmployeeContext = createContext(null);

const employeeApi = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

employeeApi.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('employee_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

employeeApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const msg = error.response?.data?.error || '';
      if (msg.includes('Session expired') || msg.includes('Invalid token') || msg.includes('Session revoked')) {
        sessionStorage.removeItem('employee_token');
        sessionStorage.removeItem('employee_data');
        window.location.href = '/employee/login';
      }
    }
    return Promise.reject(error);
  }
);

export const EmployeeProvider = ({ children }) => {
  const [employee, setEmployee] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadEmployee = useCallback(async () => {
    const token = sessionStorage.getItem('employee_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await employeeApi.get('/employee/auth/me');
      setEmployee(res.data.employee);
      setPermissions(res.data.permissions || []);
    } catch {
      sessionStorage.removeItem('employee_token');
      sessionStorage.removeItem('employee_data');
      setEmployee(null);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEmployee(); }, [loadEmployee]);

  const login = async (token, empData, mustChange) => {
    sessionStorage.setItem('employee_token', token);
    sessionStorage.setItem('employee_data', JSON.stringify(empData));
    setEmployee(empData);
    return { must_change_password: mustChange };
  };

  const logout = async () => {
    try { await employeeApi.post('/employee/auth/logout'); } catch {}
    sessionStorage.removeItem('employee_token');
    sessionStorage.removeItem('employee_data');
    setEmployee(null);
    setPermissions([]);
  };

  const hasPermission = (module, action) => {
    return permissions.some((p) => p.module === module && p.action === action);
  };

  return (
    <EmployeeContext.Provider value={{ employee, permissions, loading, login, logout, hasPermission, loadEmployee }}>
      {children}
    </EmployeeContext.Provider>
  );
};

export const useEmployee = () => {
  const ctx = useContext(EmployeeContext);
  if (!ctx) throw new Error('useEmployee must be used within EmployeeProvider');
  return ctx;
};

export { employeeApi };
export default useEmployee;
